import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Lock } from "lucide-react";
import { api, errMsg, roleStyle } from "../lib/api";
import { useRoles } from "../context/RolesContext";
import { PageHeader, Panel, btnPrimary, inputCls } from "../components/ui-kit";

const COLORS = ["#FF6B00", "#3B82F6", "#10B981", "#F59E0B", "#A855F7", "#06B6D4", "#EC4899", "#F43F5E", "#84CC16", "#94A3B8"];

const ColorPicker = ({ value, onChange, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {COLORS.map((c) => (
      <button key={c} type="button" data-testid={`${testId}-${c.slice(1)}`} onClick={() => onChange(c)}
        className={`h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 ${value === c ? "border-white" : "border-transparent"}`} style={{ background: c }} />
    ))}
  </div>
);

const RoleRow = ({ r, reload }) => {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ label: r.label, color: r.color });
  const save = async () => {
    try {
      await api.patch(`/roles/${r.key}`, { label: f.label, color: f.color });
      toast.success("Employee type updated");
      setEdit(false);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async () => {
    if (!window.confirm(`Delete employee type "${r.label}"?`)) return;
    try {
      await api.delete(`/roles/${r.key}`);
      toast.success("Employee type deleted");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel data-testid={`role-row-${r.key}`} className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
      {edit ? (
        <div className="flex-1 space-y-3">
          <input data-testid={`role-edit-label-${r.key}`} className={inputCls} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
          <ColorPicker value={f.color} onChange={(color) => setF({ ...f, color })} testId={`role-edit-color-${r.key}`} />
        </div>
      ) : (
        <div className="flex flex-1 items-center gap-3">
          <span className="rounded-md border px-2.5 py-1 font-mono text-xs uppercase tracking-wider" style={roleStyle(r.color)} data-testid={`role-label-${r.key}`}>{r.label}</span>
          <span className="font-mono text-[10px] text-slate-600">{r.key}</span>
          {r.system && <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><Lock className="h-3 w-3" />Built in: can assign tasks</span>}
        </div>
      )}
      <div className="flex gap-2">
        {edit ? (
          <>
            <button data-testid={`role-save-${r.key}`} onClick={save} className="rounded-lg bg-emerald-500/15 p-2 text-emerald-300 hover:bg-emerald-500/25"><Check className="h-4 w-4" /></button>
            <button data-testid={`role-cancel-${r.key}`} onClick={() => { setEdit(false); setF({ label: r.label, color: r.color }); }} className="rounded-lg p-2 text-slate-500 hover:bg-[#1C202E]"><X className="h-4 w-4" /></button>
          </>
        ) : (
          <>
            <button data-testid={`role-edit-${r.key}`} onClick={() => setEdit(true)} className="rounded-lg p-2 text-slate-500 hover:bg-[#1C202E] hover:text-white"><Pencil className="h-4 w-4" /></button>
            {!r.system && <button data-testid={`role-delete-${r.key}`} onClick={del} className="rounded-lg p-2 text-slate-500 hover:bg-[#1C202E] hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>}
          </>
        )}
      </div>
    </Panel>
  );
};

export default function Roles() {
  const { roles, reload } = useRoles();
  const [f, setF] = useState({ label: "", color: COLORS[6] });
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/roles", f);
      toast.success(`"${f.label}" added`);
      setF({ label: "", color: COLORS[6] });
      reload();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Team setup" title="Employee types" desc="Add, rename or recolour the employee types. They show up when you assign roles to people and when you pick step owners in processes." />
      <Panel className="mb-8 p-6">
        <form onSubmit={add} className="flex flex-col gap-4 md:flex-row md:items-center">
          <input required placeholder="New employee type, e.g. Video Editor" data-testid="role-new-label-input" className={`${inputCls} md:max-w-sm`} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
          <ColorPicker value={f.color} onChange={(color) => setF({ ...f, color })} testId="role-new-color" />
          <button data-testid="role-add-button" className={`${btnPrimary} md:ml-auto`}><Plus className="h-4 w-4" />Add type</button>
        </form>
      </Panel>
      <div className="space-y-3" data-testid="roles-list">{roles.map((r) => <RoleRow key={`${r.key}-${r.label}-${r.color}`} r={r} reload={reload} />)}</div>
    </div>
  );
}
